import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@company-box/models';

export type AiImageAspectRatio = '1:1' | '9:16' | '16:9' | '4:3' | '3:4';

// Backend contract: generated images should be stored in the normal image pipeline,
// and `image.src` should be a stored PNG URL the Unity designer can load as a standard image asset.
export interface AiImageGenerationRequest {
  containerId: string;
  prompt: string;
  aspectRatio: AiImageAspectRatio;
  fileType: 'png';
  conversationId?: string;
}

export interface AiGeneratedImage {
  name: string;
  src: string;
  contentType?: {
    type: string;
    subtype?: string;
  };
  length?: string;
  created?: string;
  updated?: string;
}

export interface AiImageGenerationResponse {
  image: AiGeneratedImage;
  conversationId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ImagesService {

  private environment = inject(ENVIRONMENT);
  constructor(
    private httpClient: HttpClient
  ) { }


  upload(url: string, formData: FormData) {
    console.log('The file is: ', formData.get('file'));
    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(url, formData)
        .subscribe(res => {
          resolve(res);
        }, error => {
          reject(error.message || error);
        });
    });
  }

  uploadPixabay(containerId: string, pixabayUrl: string) {
    const url = `${this.environment.apiHost}/zuul/converter/upload/${containerId}/url?url=${pixabayUrl}`;
    return new Promise((resolve, reject) => {
      this.httpClient.post(url, {}).subscribe(response => {
        resolve(response);
      }, error => {
        reject(error);
      });
    });
  }

  generateAiImage(userID: number, request: AiImageGenerationRequest): Promise<AiImageGenerationResponse> {
    const url = `${this.environment.apiHost}/project/ai/${userID}/image`;
    return new Promise<AiImageGenerationResponse>((resolve, reject) => {
      this.httpClient.post<AiImageGenerationResponse>(url, request)
        .subscribe(response => {
          resolve(response);
        }, error => {
          if (error instanceof HttpErrorResponse) {
            const message = this.extractAiImageErrorMessage(error.error);
            if (message) {
              reject(new Error(message));
              return;
            }
            if (error.status >= 400 && error.status < 500) {
              reject(new Error('The request was rejected.'));
              return;
            }
          }
          reject(error);
        });
    });
  }

  // The project service can serialize a caught exception as { "<exception message>": { message, ... } };
  // the useful text is nested one level under a key we don't know ahead of time.
  private extractAiImageErrorMessage(body: any): string {
    if (typeof body === 'string') {
      return body;
    }
    if (!body || typeof body !== 'object') {
      return '';
    }
    if (typeof body.message === 'string' && body.message) {
      return body.message;
    }
    if (typeof body.error === 'string' && body.error) {
      return body.error;
    }
    const keys = Object.keys(body);
    if (keys.length === 1) {
      const nested = body[keys[0]];
      if (nested && typeof nested === 'object' && typeof nested.message === 'string' && nested.message) {
        return nested.message;
      }
    }
    return '';
  }

  convertContentFromUrl(containerId: string, url: string, target?: string) {
    target = target || 'application/svg%2Bxml';
    const fullUrl = `${this.environment.apiHost}/converter/convert/url?target=${target}&url=${url}`;

    return new Promise((resolve, reject) => {
      this.httpClient.post(fullUrl, {}).subscribe(res => {
        resolve(res);
      }, error => {
        reject(error);
      });
    });
  }

  searchPixabay(url: string) {
    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url)
        .subscribe((response: any) => {
          resolve(response);
        }, error => {
          reject(error.message || error);
        });
    });
  }

  getUserImages(userID: number) {
    const url = `${this.environment.apiHost}/project/image/${userID}/list?filter=.*.jpg%24%7C.*.png%24%7C.*.gif%24%7C.*.svg%24&sort=-updated`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url)
        .subscribe((response: any) => {
          resolve(response);
        }, error => {
          reject(error.message || error);
        });
    });
  }

  getContainerFiles(containerId: string) {
    const filter = encodeURIComponent('.*upload/.*\.jpg$|.*upload/.*\.png$|.*upload/.*\.gif$|.*upload/.*\.svg$');
    const url = `${this.environment.apiHost}/project/container/${containerId}/files?includeContent=false&filter=${filter}&sort=-updated`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url)
        .subscribe(response => {
          resolve(response);
        }, error => {
          reject(error.message || error);
        });
    });
  }

  uploadImageToUserLibrary(file: any, userID: number) {
    const url = `${this.environment.apiHost}/project/image/${userID}`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(url, file)
        .subscribe((response: any) => {
          resolve(response);
        }, error => {
          reject(error.message || error);
        });
    });
  }

  uploadImageToContainer(file: any, containerId: string) {
    const url = `${this.environment.apiHost}/project/container/${containerId}/image`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(url, file)
        .subscribe(response => {
          resolve(response);
        }, error => {
          reject(error.message || error);
        });
    });
  }

  copyImageToContainer(file: any, containerId: string) {
    const url = `${this.environment.apiHost}/project/container/${containerId}/image`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(url, file)
        .subscribe(response => {
          resolve(response);
        }, error => {
          reject(error.message || error);
        });
    });
  }

  copyFromContainer(containerId: string, userId: number) {
    const url = `${this.environment.apiHost}/project/image/copy/${containerId}/${userId}`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(url, {})
        .subscribe(response => {
          resolve(response);
        }, error => {
          reject(error.message || error);
        });
    });
  }

  removeImage(userId: number, image: any) {
    const url = `${this.environment.apiHost}/project/image/${userId}/${image.name}`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.delete(url)
        .subscribe(response => {
          resolve(response);
        }, error => {
          reject(error.message || error);
        });
    });
  }

  deleteContainerImage(containerId: string, image: any) {
    const url = `${this.environment.apiHost}/project/container/${containerId}/upload/${image.name}`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.delete(url)
        .subscribe(response => {
          resolve(response);
        }, error => {
          reject(error.message || error);
        });
    });
  }


  saveImageToContainer(containerId: string, filename: string, fileContent: any) {
    const url = `${this.environment.apiHost}/project/container/${containerId}/upload/json?filename=${filename}`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(url, fileContent)
        .subscribe(response => {
          resolve(response);
        }, error => {
          reject(error.message || error);
        });
    });

  }


getFileContent(containerId: string, filename: string, bustCache: boolean = false) {
  let url = `${this.environment.apiHost}/project/container/${containerId}/files/content?filename=${filename}`;
  if (bustCache) {
    url += `&v=${Date.now()}`;
  }

  return new Promise<any>((resolve, reject) => {
    this.httpClient.get(url)
      .subscribe((response: any) => {
        resolve(response);
      }, error => {
        reject(error.message || error);
      });
  });
}
}
